import { useCallback, useState } from "react";

/**
 * Track a set of card IDs that are "expanded" (i.e. showing their poster
 * image). Cards default to *collapsed* — the Set starts empty.
 *
 * Returned:
 *   isExpanded(id)  — true when the caller's card is expanded.
 *   toggle(id)      — flip a card between collapsed / expanded.
 *   expandAll(ids)  — reveal a specific batch.
 *   collapseAll()   — hide every image.
 */
export function useCardExpansion() {
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const isExpanded = useCallback(
    (id) => expandedIds.has(id),
    [expandedIds],
  );

  const toggle = useCallback((id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAll = useCallback((ids) => {
    setExpandedIds(new Set(ids));
  }, []);

  const collapseAll = useCallback(() => {
    setExpandedIds(new Set());
  }, []);

  return { isExpanded, toggle, expandAll, collapseAll };
}
